import { Stack, Grid, Typography, Box } from "@mui/material";
import { InnerBox } from "@/components/elements/InnerBox";
import { black, text } from "@/theme/colors";
import { CaseInfoItem } from "@/features/case/components/CaseBasicInfo/CaseInfoItem";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useMemo } from "react";
import { DATE_FORMAT, YEAR_MONTH_DAY_STR_FORMAT } from "@/constants/datetime";
import dayjs from "dayjs";
import { EMPTY_STR } from "@/constants/string";
import { typography } from "@/theme/typography";
import { TSharedCaseMatch } from "@/features/sharing/types/shared-case";
import { TTalentUploadFile } from "@/features/talent/types/talentForm";
import { SharedTalentFileItem } from "@/features/sharing/components/SharedTalentFileItem";
import { CaseMatchTagList } from "@/features/case/components/CaseMatchTagList";

type TSharedCaseMatchDetailInfoProps = {
  caseMatch?: TCaseMatches;
  talent?: Partial<TTalentForm>;
  casePersonInChargeName?: string;
  talentPersonInChargeName?: string;
  shareUrl?: string;
};

/** Readonly case match detail for shared (public) view — no useGetUserListPersonInCharge, hides salesFee/purchaseFee */
export const SharedCaseMatchDetailInfo: React.FC<TSharedCaseMatchDetailInfoProps> = ({
  caseMatch,
  talent,
  casePersonInChargeName: casePersonInChargeNameProp,
  talentPersonInChargeName: talentPersonInChargeNameProp,
  shareUrl,
}) => {
  // Resolve person-in-charge: prefer prop, fallback to runtime data from shared case match
  const sharedCaseMatch = caseMatch as unknown as TSharedCaseMatch | undefined;
  const casePersonInChargeName = casePersonInChargeNameProp || sharedCaseMatch?.caseMatchUser?.fullName || EMPTY_STR.TEXT;
  const talentPersonInChargeName = talentPersonInChargeNameProp || talent?.personInCharge?.fullName || EMPTY_STR.TEXT;

  const proposalDate = caseMatch?.proposalDate ? dayjs(caseMatch.proposalDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
  const interviewDate = caseMatch?.interviewDate ? dayjs(caseMatch.interviewDate).format(DATE_FORMAT) : EMPTY_STR.DATE;

  // Resolve language proficiency directly from cert data (no auth API needed)
  // BE response includes languageTest.testName and languageTestLevel.level on each cert
  const japaneseLanguageProficiency = useMemo(() => {
    if (!talent?.languageCertificates || talent.languageCertificates.length === 0) return EMPTY_STR.TEXT;
    const labels = talent.languageCertificates
      .map((cert) => {
        const certAny = cert as Record<string, unknown>;
        const testName = (certAny.languageTest as { testName?: string })?.testName;
        const levelName = cert.languageTestLevel?.level;
        if (testName && levelName) return `${testName} ・ ${levelName}`;
        if (testName) return testName;
        if (levelName) return levelName;
        return null;
      })
      .filter(Boolean);
    return labels.length > 0 ? labels.join(", ") : EMPTY_STR.TEXT;
  }, [talent]);

  const majorName = useMemo(() => talent?.major?.majorName || EMPTY_STR.TEXT, [talent]);
  const jobTypeName = useMemo(() => talent?.jobType?.jobName || EMPTY_STR.TEXT, [talent]);

  const companyOfferDate = caseMatch?.companyOfferDate ? dayjs(caseMatch.companyOfferDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
  const offerAcceptDate = caseMatch?.offerAcceptDate ? dayjs(caseMatch.offerAcceptDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
  const joinDateExpected = caseMatch?.joinDateExpected ? dayjs(caseMatch.joinDateExpected).format(DATE_FORMAT) : EMPTY_STR.DATE;
  const companyJoiningDate = caseMatch?.companyJoiningDate ? dayjs(caseMatch.companyJoiningDate).format(DATE_FORMAT) : EMPTY_STR.DATE;

  const getFilesByType = (typeIds: number[]) => {
    if (!talent?.talentUploadFiles || talent.talentUploadFiles.length === 0) return [];
    return talent.talentUploadFiles.filter((f) => {
      return f.attachmentDocumentTypeIds?.some((id) => typeIds.includes(id));
    });
  };

  const japaneseLanguageCertificateFiles = getFilesByType([7]);
  const specifiedSkillCertificateFiles = getFilesByType([5]);
  const residenceCardFiles = getFilesByType([1, 2]);

  // File display with download/preview for shared view (uses shared signed URL API)
  const renderFileSection = (label: string, files: TTalentUploadFile[]) => (
    <Grid item xs={12}>
      <Stack sx={{ borderBottom: "1px solid #E5E5E5", pb: 2, mt: files === japaneseLanguageCertificateFiles ? 0 : 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
          <Typography variant="cap12" color={black[70]}>
            {label}
          </Typography>
        </Stack>
        {files.length > 0 ? (
          <Stack spacing={2}>
            {files.map((item, idx) => (
              <Box key={idx} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                {shareUrl ? (
                  <SharedTalentFileItem filePath={item.filePath} shareUrl={shareUrl} files={files} currentFileIndex={idx} maxWidth={336} />
                ) : (
                  <Typography variant="body14" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.filePath}
                  </Typography>
                )}
                <Typography variant="body2" color={text.secondary} sx={{ whiteSpace: "nowrap", pl: 1, ...typography.cap12, color: text.secondary }}>
                  {item.updatedAt ? dayjs(item.updatedAt).format(YEAR_MONTH_DAY_STR_FORMAT) : ""} 更新
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body14Semi">{EMPTY_STR.TEXT}</Typography>
        )}
      </Stack>
    </Grid>
  );

  return (
    <InnerBox mt={3} p={3} showDivider={false} borderColor={black[95]} bordered={true}>
      <Stack>
        <Box mb={3}>
          <Typography variant="sub16Bold" color={black[30]}>
            マッチング情報
          </Typography>
        </Box>

        <Grid
          container
          rowSpacing={1}
          columnSpacing={3}
          alignItems="stretch"
          sx={{
            "& > .MuiGrid-item > *:first-child": { height: "100%" },
            "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" },
          }}
        >
          <Grid item xs={6}>
            <CaseInfoItem label="案件マッチング担当者" value={casePersonInChargeName} />
          </Grid>
          <Grid item xs={6}>
            <CaseInfoItem label="人材マッチング担当者" value={talentPersonInChargeName} />
          </Grid>
          <Grid item xs={6}>
            <CaseInfoItem label="推薦日" value={proposalDate} />
          </Grid>
          <Grid item xs={6}>
            <CaseInfoItem label="面接日" value={interviewDate} />
          </Grid>
          <Grid item xs={12}>
            <CaseInfoItem label="日本語能力証明書等（実習修了証明含む）" value={japaneseLanguageProficiency} />
          </Grid>
          {renderFileSection("日本語能力証明書ファイル", japaneseLanguageCertificateFiles)}
          <Grid item xs={12}>
            <CaseInfoItem label="特定技能合格証等" value={talent?.specificSkillCertificateStr || EMPTY_STR.TEXT} />
          </Grid>
          {renderFileSection("特定技能合格証等ファイル", specifiedSkillCertificateFiles)}
          <Grid item xs={6} mt={1}>
            <CaseInfoItem label="技能実習　職種" value={majorName} />
          </Grid>
          <Grid item xs={6} mt={1}>
            <CaseInfoItem label="作業" value={jobTypeName} />
          </Grid>
          <Grid item xs={12} mt={1}>
            <Stack gap={1}>
              <Typography variant="cap12" color={black[70]}>
                タグ
              </Typography>
              <CaseMatchTagList tagIds={caseMatch?.tagIds} wrap />
            </Stack>
          </Grid>
          {renderFileSection("在留カード（表・裏）", residenceCardFiles)}
          <Grid item xs={12}>
            <CaseInfoItem label="適合度メモ" value={caseMatch?.fitNote || EMPTY_STR.TEXT} type="textArea" />
          </Grid>
          <Grid item xs={6}>
            <CaseInfoItem label="企業内定日" value={companyOfferDate} />
          </Grid>
          <Grid item xs={6}>
            <CaseInfoItem label="内定受諾日" value={offerAcceptDate} />
          </Grid>
          <Grid item xs={12}>
            <CaseInfoItem label="企業入社日（予定）" value={joinDateExpected} />
          </Grid>
          <Grid item xs={12}>
            <CaseInfoItem label="企業入社日" value={companyJoiningDate} isShowDivider={false} />
          </Grid>
        </Grid>
      </Stack>
    </InnerBox>
  );
};
